<script setup>
import { ref, watch } from 'vue'
import { useAgentChat } from '@/composables/useAgentChat'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  conversationId: {
    type: String,
    default: null,
  },
  msgDbIndex: {
    type: Number,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue'])

const { fetchTrace } = useAgentChat()

const traceData = ref(null)
const loading = ref(false)
const fetchError = ref(null)

watch(() => props.modelValue, async val => {
  if (val && props.conversationId != null && props.msgDbIndex != null) {
    loading.value = true
    fetchError.value = null
    traceData.value = null
    try {
      traceData.value = await fetchTrace(props.conversationId, props.msgDbIndex)
    } catch (err) {
      fetchError.value = err?.response?.data?.error || 'Failed to load trace data'
    } finally {
      loading.value = false
    }
  }
})

function close() {
  emit('update:modelValue', false)
}

function formatTime(isoStr) {
  if (!isoStr) return ''
  
  return new Date(isoStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function formatDuration(ms) {
  if (ms == null) return ''
  if (ms < 1000) return `${ms}ms`
  
  return `${(ms / 1000).toFixed(2)}s`
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="700"
    scrollable
    @update:model-value="emit('update:modelValue', $event)"
  >
    <VCard>
      <VCardTitle class="d-flex align-center justify-space-between pa-4">
        <div class="d-flex align-center ga-2">
          <VIcon
            icon="mdi-chart-timeline-variant"
            color="primary-dark"
            size="20"
          />
          <span class="text-h6">Agent Trace</span>
        </div>
        <VBtn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="close"
        />
      </VCardTitle>

      <VDivider />

      <VCardText class="pa-4">
        <!-- Loading -->
        <div
          v-if="loading"
          class="d-flex justify-center pa-6"
        >
          <VProgressCircular
            indeterminate
            color="primary-dark"
          />
        </div>

        <!-- Error -->
        <VAlert
          v-else-if="fetchError"
          type="error"
          variant="tonal"
          class="mb-0"
        >
          {{ fetchError }}
        </VAlert>

        <!-- Trace data -->
        <div v-else-if="traceData">
          <!-- Summary row -->
          <div class="trace-summary d-flex flex-wrap ga-4 mb-4 pa-3 rounded">
            <div class="trace-stat">
              <div class="text-caption text-medium-emphasis">
                Latency
              </div>
              <div class="text-body-1 font-weight-medium">
                {{ formatDuration(traceData.latencyMs) }}
              </div>
            </div>
            <div class="trace-stat">
              <div class="text-caption text-medium-emphasis">
                Tools Used
              </div>
              <div class="text-body-1 font-weight-medium">
                {{ traceData.toolsUsed }}
              </div>
            </div>
            <div class="trace-stat">
              <div class="text-caption text-medium-emphasis">
                Response Length
              </div>
              <div class="text-body-1 font-weight-medium">
                {{ traceData.responseLength }} chars
              </div>
            </div>
            <div class="trace-stat">
              <div class="text-caption text-medium-emphasis">
                Prompt Tokens
              </div>
              <div class="text-body-1 font-weight-medium">
                {{ traceData.totalPromptTokens ?? '—' }}
              </div>
            </div>
            <div class="trace-stat">
              <div class="text-caption text-medium-emphasis">
                Completion Tokens
              </div>
              <div class="text-body-1 font-weight-medium">
                {{ traceData.totalCompletionTokens ?? '—' }}
              </div>
            </div>
          </div>

          <!-- Timeline -->
          <div class="trace-timeline">
            <!-- Started -->
            <div class="trace-event mb-3">
              <div class="d-flex align-center ga-2 mb-1">
                <VIcon
                  icon="mdi-play-circle-outline"
                  color="success"
                  size="18"
                />
                <span class="text-body-2 font-weight-medium">Request Started</span>
                <span class="text-caption text-medium-emphasis ml-auto">{{ formatTime(traceData.startedAt) }}</span>
              </div>
            </div>

            <!-- Tool calls -->
            <div
              v-for="(tc, idx) in traceData.toolCalls"
              :key="idx"
              class="trace-event mb-3"
            >
              <div class="d-flex align-center ga-2 mb-2">
                <VIcon
                  icon="mdi-wrench-outline"
                  color="primary-dark"
                  size="18"
                />
                <span class="text-body-2 font-weight-medium">{{ tc.toolName }}</span>
                <VChip
                  :color="tc.success ? 'success' : 'error'"
                  size="x-small"
                  variant="tonal"
                >
                  {{ tc.success ? 'success' : 'error' }}
                </VChip>
                <span class="text-caption text-medium-emphasis">{{ formatDuration(tc.durationMs) }}</span>
                <span class="text-caption text-medium-emphasis ml-auto">{{ formatTime(tc.startedAt) }}</span>
              </div>

              <!-- Tool args -->
              <VExpansionPanels
                variant="accordion"
                class="trace-panels"
              >
                <VExpansionPanel>
                  <VExpansionPanelTitle class="text-caption py-1 px-3">
                    Input
                  </VExpansionPanelTitle>
                  <VExpansionPanelText>
                    <pre class="trace-json">{{ JSON.stringify(tc.args, null, 2) }}</pre>
                  </VExpansionPanelText>
                </VExpansionPanel>
                <VExpansionPanel>
                  <VExpansionPanelTitle class="text-caption py-1 px-3">
                    Output
                  </VExpansionPanelTitle>
                  <VExpansionPanelText>
                    <pre class="trace-json">{{ JSON.stringify(tc.result, null, 2) }}</pre>
                  </VExpansionPanelText>
                </VExpansionPanel>
              </VExpansionPanels>
            </div>

            <!-- Response generated -->
            <div class="trace-event">
              <div class="d-flex align-center ga-2">
                <VIcon
                  icon="mdi-check-circle-outline"
                  color="success"
                  size="18"
                />
                <span class="text-body-2 font-weight-medium">Response Generated</span>
                <span class="text-caption text-medium-emphasis ml-auto">{{ formatTime(traceData.endedAt) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty -->
        <div
          v-else
          class="text-body-2 text-medium-emphasis text-center pa-6"
        >
          No trace data available.
        </div>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style scoped>
.trace-summary {
  background: var(--ds-brand-primary-light);
  border: 1px solid var(--ds-border-default);
}

.trace-stat {
  min-width: 100px;
}

.trace-timeline {
  border-left: 2px solid var(--ds-border-default);
  padding-left: 1rem;
}

.trace-event {
  position: relative;
}

.trace-event::before {
  content: '';
  position: absolute;
  left: -1.35rem;
  top: 0.3rem;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ds-border-default);
}

.trace-panels {
  margin-top: 4px;
}

.trace-panels :deep(.v-expansion-panel-title) {
  min-height: 32px !important;
  font-size: 0.75rem;
}

.trace-json {
  font-size: 0.75rem;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
  color: var(--ds-text-primary);
  font-family: monospace;
  max-height: 300px;
  overflow-y: auto;
}
</style>
